import { cn, DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, Icon } from '@mntn/ui-components'
import { useCallback } from 'react'
import type { OrderItem } from '@/flight-deck-api/services/order-items.service'
import { SLATE_THEME_CLASSNAME } from './mfe-theme-wrapper'

type CampaignGroupAction = NonNullable<OrderItem['campaignGroups'][number]['availableActions']>[number]

interface CampaignActionsMenuProps {
  campaign: OrderItem['campaignGroups'][0]
  availableActions?: CampaignGroupAction[]
  onAction: (action: CampaignGroupAction, campaign: OrderItem['campaignGroups'][0]) => void
  isLoading?: boolean
  variant?: 'dark'
}

export const CampaignActionsMenu = ({
  campaign,
  availableActions,
  onAction,
  isLoading = false,
  variant,
}: CampaignActionsMenuProps) => {
  // Campaign actions
  const getActionLabel = useCallback(
    (action: CampaignGroupAction) => {
      switch (action) {
        case 'VIEW':
          return 'View'
        case 'DUPLICATE':
          return isLoading ? 'Loading...' : 'Duplicate'
        case 'DUPLICATE_AS_EXPERIMENT':
          return isLoading ? 'Loading...' : 'Duplicate as Experiment'
        case 'ARCHIVE':
          return isLoading ? 'Loading...' : 'Archive'
        default:
          return action
      }
    },
    [isLoading],
  )

  const handleAction = useCallback(
    (action: CampaignGroupAction) => {
      // Disable actions while loading
      if (isLoading && (action === 'DUPLICATE' || action === 'DUPLICATE_AS_EXPERIMENT' || action === 'ARCHIVE')) {
        return
      }
      onAction(action, campaign)
    },
    [onAction, campaign, isLoading],
  )

  const menuItems = (availableActions || []).map(action => ({
    key: action,
    label: getActionLabel(action),
    onClick: () => handleAction(action),
  }))

  if (menuItems.length === 0) {
    return null
  }

  const buttonBgColor = variant === 'dark' ? 'hover:bg-sub-surface-bg-inverse' : 'hover:bg-gray-g1'
  const buttonIconColor = variant === 'dark' ? 'text-header-inverse' : 'text-primary'

  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          onClick={e => e.stopPropagation()}
          className={cn('p-2 transition-colors rounded-xs', buttonBgColor)}
          aria-label="Campaign actions"
        >
          <Icon name="EllipsisVertical" className={cn('w-4 h-4', buttonIconColor)} />
        </button>
      </DropdownMenuTrigger>
      {/* note: this component is rendered at the end of the document body so we need to add the slate class to the content */}
      <DropdownMenuContent
        align="end"
        className="w-32 z-50 border-none bg-background text-header"
        wrapperClassName={SLATE_THEME_CLASSNAME}
        onClick={e => e.stopPropagation()}
      >
        {menuItems.map(item => (
          <DropdownMenuItem
            key={item.key}
            onClick={item.onClick}
            className={cn(
              'focus:bg-surface-bg focus:text-header',
              item.key === 'DUPLICATE_AS_EXPERIMENT' ? 'text-error' : '',
            )}
          >
            {item.label}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
